import { accountDeferred } from '@eureka/utils/account';
import { openUsageLimitModal } from '@eureka/shared/ip-nav';

const CREDIT_BALANCE_INSUFFICIENT_ERROR_CODE_FRAGMENTS = [
    '7100002'
];
const CREDIT_BALANCE_INSUFFICIENT_ERROR_CODES = [
    'errors.com.patsnap.credits.credit_balance_insufficient'
];
const CREDIT_ERROR_MESSAGE_KEYS = [
    {
        fragment: '7000001',
        messageKey: 'errors.creditRequestTemporarilyUnavailable',
    },
    {
        fragment: '7000002',
        messageKey: 'errors.creditRequestTemporarilyUnavailable',
    }
];
const ENTERPRISE_CREDIT_ERROR_MESSAGE_KEYS = [
    {
        fragment: '7100002',
        messageKey: 'errors.enterpriseCreditsDepleted',
    },
    {
        fragment: '7200004',
        messageKey: 'errors.enterprisePersonalDailyUsageLimitExceeded',
    },
    {
        fragment: '7200005',
        messageKey: 'errors.enterpriseDailyUsageLimitExceeded',
    }
];
const ENTERPRISE_CREDIT_DAILY_LIMIT_ERROR_CODE_FRAGMENTS = ENTERPRISE_CREDIT_ERROR_MESSAGE_KEYS
    .filter(item => item.fragment !== '7100002')
    .map(item => item.fragment);
const CREDIT_ERROR_CODE_FRAGMENTS = CREDIT_ERROR_MESSAGE_KEYS.map(item => item.fragment);

function collectBackendErrorCodes(error: unknown) {
    const codes = new Set<string>();

    function walk(value: unknown, depth = 0) {
        if (!value || typeof value !== 'object' || depth > 3) return;

        const record = value as Record<string, unknown>;
        const code = record.error_code;
        if (typeof code === 'string' || typeof code === 'number') {
            codes.add(String(code));
        }

        ['data', 'cause', 'response'].forEach(key => walk(record[key], depth + 1));
    }

    walk(error);
    return codes;
}

function isCreditBalanceInsufficientError(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    return Array.from(codes).some(code => {
        return CREDIT_BALANCE_INSUFFICIENT_ERROR_CODES.includes(code) ||
            CREDIT_BALANCE_INSUFFICIENT_ERROR_CODE_FRAGMENTS.some(fragment => code.includes(fragment));
    });
}

function getCreditErrorMessageKey(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    const matchedError = Array.from(codes).map(code => {
        return ENTERPRISE_CREDIT_ERROR_MESSAGE_KEYS.find(item => code.includes(item.fragment));
    }).find(Boolean);

    return matchedError?.messageKey;
}

function getCommonCreditErrorMessageKey(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    const matchedError = Array.from(codes).map(code => {
        return CREDIT_ERROR_MESSAGE_KEYS.find(item => code.includes(item.fragment));
    }).find(Boolean);

    return matchedError?.messageKey;
}

function isCommonCreditErrorCandidate(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    return Array.from(codes).some(code => {
        return CREDIT_ERROR_CODE_FRAGMENTS.some(fragment => code.includes(fragment));
    });
}

function isEnterpriseCreditDailyLimitErrorCandidate(error: unknown) {
    const codes = collectBackendErrorCodes(error);
    return Array.from(codes).some(code => {
        return ENTERPRISE_CREDIT_DAILY_LIMIT_ERROR_CODE_FRAGMENTS.some(fragment => code.includes(fragment));
    });
}

async function isPersonalAccount() {
    const account = await accountDeferred.promise;
    return !account?._info?.company_id;
}

async function maybeOpenUsageLimitModalForPersonalCredits(error: unknown) {
    if (!isCreditBalanceInsufficientError(error)) {
        return false;
    }

    try {
        if (!await isPersonalAccount()) {
            console.debug('[UsageLimitModal] Skip insufficient credits modal for enterprise user');
            return false;
        }

        openUsageLimitModal();
        console.debug('[UsageLimitModal] Opened for personal credit balance insufficient:', {
            codes: Array.from(collectBackendErrorCodes(error)),
        });
        return true;
    } catch (accountError) {
        console.warn('[UsageLimitModal] Failed to read account before opening modal:', accountError);
        return false;
    }
}

async function getEnterpriseCreditErrorMessageKey(error: unknown) {
    const messageKey = getCreditErrorMessageKey(error);
    if (!messageKey) {
        return undefined;
    }

    try {
        if (await isPersonalAccount()) {
            return undefined;
        }
        return messageKey;
    } catch (accountError) {
        console.warn('[UsageLimitModal] Failed to read account before checking enterprise credit message:', accountError);
        return undefined;
    }
}

export {
    maybeOpenUsageLimitModalForPersonalCredits,
    getCommonCreditErrorMessageKey,
    getEnterpriseCreditErrorMessageKey,
    isCommonCreditErrorCandidate,
    isEnterpriseCreditDailyLimitErrorCandidate,
    isCreditBalanceInsufficientError,
};
